import { useTranslation } from "react-i18next";
import { Button, Icon, useSnackbar } from "@mindlercare/ui-web";
import { useCallback, useMemo } from "react";
import CONSTANTS from "core/constants";
import { SlotStatus } from "core/types";
import { ClipboardSlot, formatSlotsForClipboard } from "helpers/slots";
import { useTemplates } from "hooks/useTemplates";
import { useCalendarSlots } from "components/booking/hooks/useCalendarSlots";

export const RevisitSlotClipboard = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const { data: currentSlots = [] } = useCalendarSlots();
  const slotsForClipboard = useMemo<ClipboardSlot[]>(
    () =>
      currentSlots.map(({ status, templateId, startsAt }) => ({
        status: (status ?? SlotStatus.OPEN) as SlotStatus,
        templateId,
        startsAt,
      })),
    [currentSlots],
  );

  const { data: templates } = useTemplates();

  const getSlotTemplateName = useCallback(
    (templateId: number) => {
      const template = templates.find((temp) => temp.templateId === templateId);
      return template?.name ?? "";
    },
    [templates],
  );

  const getFutureWeekRevisits = () => {
    const currentDate = new Date().getTime() / 1000;
    const revisits: ClipboardSlot[] = slotsForClipboard.filter(
      (slot: ClipboardSlot) =>
        slot.status === SlotStatus.OPEN &&
        getSlotTemplateName(slot.templateId) === CONSTANTS.REVISIT &&
        slot.startsAt > currentDate,
    );

    if (revisits.length === 0) {
      enqueueSnackbar({ text: t("NO_AVAILABLE_REVISITS"), isError: true });
      return;
    }

    const formattedRevisits = formatSlotsForClipboard(revisits);

    enqueueSnackbar({ text: t("SAVED_TO_CLIPBOARD") });

    navigator.clipboard.writeText(formattedRevisits);
  };
  return (
    <Button
      variant="secondary-small"
      onClick={() => {
        getFutureWeekRevisits();
      }}
      testId="copy-calendar-to-clipboard"
      mr={0}
    >
      <Icon.Copy />
      {t("CLIPBOARD")}
    </Button>
  );
};
